import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type HTMLAttributes,
  type ReactNode,
} from 'react';

import {
  cn,
  Flex,
  Icon,
  ShimmeringText,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';

// The leaf module, not the `@/shared/hooks` barrel: that barrel re-exports
// `useRunChat`, which imports back into `@/shared/components`.
import { useIsOverflowing } from '@/shared/hooks/useIsOverflowing';

export type TChatActivityVariant = 'thinking' | 'tool-call';

export interface IChatActivityProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'title'> {
  /** Selects the shared thinking or tool-call presentation. */
  variant: TChatActivityVariant;
  /** User-facing activity label. */
  title: string;
  /** Whether the activity is still in progress. */
  isActive?: boolean;
  /** Whether the activity should use the error treatment. */
  isError?: boolean;
  /** Whether the disclosure can be expanded. */
  collapsible?: boolean;
  /**
   * Tightens the header's vertical padding, for copy-only rows: the default
   * plus the message list's own gap leaves one-liners floating far apart.
   */
  compact?: boolean;
  /** Uses regular, emphasized text for primary business activity. */
  prominent?: boolean;
  /** Initial disclosure state. Ignored while `expanded` is provided. */
  defaultExpanded?: boolean;
  /**
   * Controlled disclosure state. When provided the row stops owning its own,
   * and the consumer drives it through `onExpandedChange`.
   *
   * For cases where something OUTSIDE the row decides what open means. The rain
   * work block opens by re-emitting its rows into the transcript as ordinary
   * messages, so the row itself cannot hold the answer.
   *
   * A CONSUMER USING THIS WITH NO CHILDREN OWNS THE ACCESSIBLE RELATIONSHIP.
   * The header keeps `aria-expanded`, which is still true, but the content it
   * refers to lives outside this component and has no single element to name,
   * so `aria-controls` cannot be supplied from here. Anything relying on that
   * association has to establish it itself.
   */
  expanded?: boolean;
  /** Fires with the requested state when the header is activated. */
  onExpandedChange?: (expanded: boolean) => void;
  /** Leading icon used by the tool-call variant. */
  icon?: ComponentProps<typeof Icon>['type'];
  /**
   * A short label beside the title. Outside the shimmer, which animates the
   * title string and restarts whenever it changes.
   */
  titleSuffix?: ReactNode;
  /** Content that remains visible below the header. */
  statusContent?: ReactNode;
  /** Expandable activity details. */
  children?: ReactNode;
  /**
   * Classes for the scrolling body, to override its defaults.
   *
   * The body caps at 200px and scrolls, which is right for tool payloads — a
   * 400-line JSON result should not push the rest of the transcript off screen.
   * It is wrong for a DRAWING: the Context Graph card renders a picture whose
   * height is a function of how many records were found, and clipping that to
   * 200px put a scrollbar through the middle of the evidence. Such a card passes
   * `max-h-none` here rather than every other card losing its cap.
   */
  bodyClassName?: string;
  /**
   * Collapse automatically when the activity finishes (default true). Ignored
   * while `expanded` is supplied — see the effect for why.
   *
   * Right for progress: a tool call that has run is a line of history, and
   * leaving twelve of them open buries the conversation. Wrong for a card whose
   * FINISHED state is the thing worth seeing — the Context Graph evidence goes
   * `running` → `success`, and the auto-collapse fired exactly as its picture
   * became available, so opening it by default was silently undone a moment later.
   */
  collapseOnComplete?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * Shared activity disclosure used for thinking and tool-call progress in chat.
 *
 * Both variants share the same spacing, title treatment, chevron, keyboard
 * interaction, and expansion animation. Tool calls add an icon and may supply
 * payload or approval content through the provided slots.
 */
export function ChatActivity({
  variant,
  title,
  titleSuffix,
  isActive = false,
  isError = false,
  collapsible = false,
  compact = false,
  prominent = false,
  defaultExpanded = isActive,
  expanded: expandedProp,
  onExpandedChange,
  icon = 'Wrench',
  bodyClassName,
  collapseOnComplete = true,
  statusContent,
  children,
  className,
  'data-testid': dataTestId = 'chat-activity',
  ...props
}: IChatActivityProps) {
  const [internalExpanded, setInternalExpanded] = useState(
    collapsible && defaultExpanded,
  );
  const isControlled = expandedProp !== undefined;
  const isExpanded = isControlled ? expandedProp : internalExpanded;
  // A caller with nothing to disclose gets a header and no body (see below).
  const hasBody = Boolean(children);
  const isThinking = variant === 'thinking';
  const titleRef = useRef<HTMLDivElement>(null);
  // Tool-call titles only (see below). Vertical axis: the box is pinned to two
  // lines and `break-words` keeps a long unbreakable token from reporting
  // horizontal overflow. `title` is the re-measure key because a clamped box
  // keeps its height while its text is replaced in place.
  const isTitleClipped = useIsOverflowing(titleRef, 'vertical', title);
  const previousIsActiveRef = useRef(isActive);
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isUserScrolledUpRef = useRef(false);

  const handleToggleExpand = useCallback(() => {
    if (!collapsible) return;
    const next = !isExpanded;
    if (!isControlled) setInternalExpanded(next);
    onExpandedChange?.(next);
  }, [collapsible, isControlled, isExpanded, onExpandedChange]);

  const handleScroll = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;
    const isAtBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight < 20;
    isUserScrolledUpRef.current = !isAtBottom;
  }, []);

  /*
   * Match ChatThinkingMessage: collapse after an active activity completes.
   *
   * Inert while CONTROLLED. Requesting a close here is indistinguishable from
   * the user clicking, so a consumer that derives its default from the activity
   * itself ("open while running") would record an explicit `false` on every
   * settle and pin the row shut forever. A controlled consumer already owns the
   * lifecycle; this is the uncontrolled convenience.
   */
  // eslint-disable-next-line no-restricted-syntax -- reason: Synchronizes disclosure UI state with the activity lifecycle transition
  useEffect(() => {
    if (
      !isControlled &&
      collapseOnComplete &&
      previousIsActiveRef.current &&
      !isActive
    ) {
      setInternalExpanded(false);
    }
    previousIsActiveRef.current = isActive;
  }, [collapseOnComplete, isActive, isControlled]);

  // eslint-disable-next-line no-restricted-syntax -- reason: Resets imperative scroll tracking when the disclosure closes
  useEffect(() => {
    if (!isExpanded) {
      isUserScrolledUpRef.current = false;
    }
  }, [isExpanded]);

  // Keep streaming details pinned to the latest content unless the user
  // scrolls up. Observing the scroll container's size fires only when content
  // actually grows, rather than on every parent re-render (children is a fresh
  // ReactNode reference each render).
  // eslint-disable-next-line no-restricted-syntax -- reason: Follows streaming activity growth via ResizeObserver instead of a render-driven dependency
  useEffect(() => {
    const scrollElement = scrollRef.current;
    const contentElement = contentRef.current;
    if (!isActive || !isExpanded || !scrollElement || !contentElement) return;
    // Observe the growing content, not the scroll container, whose box is
    // clamped by max-height once it overflows.
    const observer = new ResizeObserver(() => {
      if (!isUserScrolledUpRef.current) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    });
    observer.observe(contentElement);
    return () => observer.disconnect();
  }, [isActive, isExpanded]);

  return (
    <Flex
      direction="column"
      align="stretch"
      overflow="hidden"
      width="full"
      className={cn(
        'min-w-0 min-h-0 rounded-sm transition-colors duration-200 pr-8',
        className,
      )}
      data-testid={dataTestId}
      data-slot="chat-activity"
      data-variant={variant}
      data-expanded={isExpanded ? 'true' : 'false'}
      data-error={isError ? 'true' : 'false'}
      {...props}
    >
      <Flex
        align="center"
        justify="start"
        gap={1}
        width="full"
        paddingX={2}
        paddingY={compact ? 0.5 : 2}
        onClick={handleToggleExpand}
        className={cn(
          'group rounded-sm transition-colors',
          collapsible ? 'cursor-pointer' : 'cursor-default',
        )}
        role={collapsible ? 'button' : undefined}
        aria-expanded={collapsible ? isExpanded : undefined}
        tabIndex={collapsible ? 0 : -1}
        data-testid={`${dataTestId}-toggle`}
        onKeyDown={event => {
          if (collapsible && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            handleToggleExpand();
          }
        }}
      >
        <Flex align="center" gap={2} flex="1" className="min-w-0">
          {!isThinking && (
            <Icon
              type={icon}
              className={cn(
                'size-3 shrink-0 self-center',
                // A caller asking for the spinner glyph wants it spinning; a
                // frozen one reads as a state rather than as progress.
                icon === 'Loader2' && 'animate-spin',
                isError
                  ? 'text-warning'
                  : 'text-muted-foreground group-hover:text-foreground transition-colors duration-200',
              )}
            />
          )}
          {isThinking ? (
            /* The thinking variant is the shared spinner, which both this chat
               and the legacy one render. Its label is a fixed short string, so
               it keeps the original single-line treatment and nothing below
               reaches it. */
            <ShimmeringText
              text={title}
              duration={1.2}
              className="truncate italic text-xs"
              data-testid={`${dataTestId}-title`}
            />
          ) : (
            /* Only `TooltipContent` is conditional — swapping the trigger's
               element on overflow would remount the title mid-shimmer. */
            <Tooltip>
              <TooltipTrigger asChild>
                {/* The clamp lives here, not on the text, so the measured box
                    is the one that overflows. That makes this box own the
                    ellipsis and the line boxes, so it carries the text's own
                    typography — colour and opacity, or the "…" renders at full
                    foreground; size, or the clamp lays out 24px lines around
                    16px text and the two states settle at different heights.
                    `line-clamp-*` brings its own `display`, so it displaces
                    Flex's — see the class-list assertion in the tests. */}
                <Flex
                  ref={titleRef}
                  className={cn(
                    'min-w-0 line-clamp-2 break-words transition-colors duration-200',
                    prominent ? 'text-sm font-medium' : 'text-xs italic',
                    isError
                      ? 'text-warning'
                      : prominent
                        ? 'text-foreground'
                        : 'text-muted-foreground',
                    // Only the settled state is dimmed, and only here: nesting
                    // a second `opacity-80` inside would multiply to 0.64.
                    !isActive &&
                      !prominent &&
                      'opacity-80 group-hover:text-foreground',
                  )}
                  data-testid={`${dataTestId}-title-trigger`}
                >
                  {isActive ? (
                    <ShimmeringText
                      text={title}
                      duration={1.2}
                      className={
                        prominent ? 'text-sm font-medium' : 'italic text-xs'
                      }
                      data-testid={`${dataTestId}-title`}
                    />
                  ) : (
                    <Text
                      as="span"
                      level={prominent ? 'small' : 'xSmall'}
                      weight={prominent ? 'medium' : 'normal'}
                      className={cn('text-inherit', !prominent && 'italic')}
                      data-testid={`${dataTestId}-title`}
                    >
                      {title}
                    </Text>
                  )}
                </Flex>
              </TooltipTrigger>
              {isTitleClipped && (
                <TooltipContent
                  className="max-w-xs break-words"
                  data-testid={`${dataTestId}-title-tooltip`}
                >
                  {title}
                </TooltipContent>
              )}
            </Tooltip>
          )}
          {titleSuffix !== undefined && (
            <Text
              as="span"
              level="xSmall"
              color="muted"
              className="shrink-0 italic tabular-nums"
              data-testid={`${dataTestId}-title-suffix`}
            >
              {titleSuffix}
            </Text>
          )}
        </Flex>

        {collapsible && (
          <Icon
            type="ChevronRight"
            className={cn(
              'size-3 shrink-0 text-muted-foreground group-hover:text-foreground transition-all duration-200 opacity-80',
              isExpanded && 'rotate-90',
            )}
            data-testid={`${dataTestId}-chevron`}
          />
        )}
      </Flex>

      {statusContent}

      {/* A header-only disclosure is legitimate — the rain work block renders its
          rows as siblings — and an empty body would still paint its tinted box. */}
      {collapsible && hasBody && (
        <div
          className={cn(
            'grid transition-[grid-template-rows,opacity] duration-200 ease-in-out',
            isExpanded
              ? 'grid-rows-[1fr] opacity-100 pb-3'
              : 'grid-rows-[0fr] opacity-0',
          )}
          data-testid={`${dataTestId}-content`}
        >
          <div
            className={cn(
              'min-w-0 min-h-0 w-full overflow-hidden gap-2',
              isExpanded && 'rounded-sm',
            )}
          >
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className={cn(
                'w-full max-h-[200px] overflow-y-auto p-3 rounded-lg bg-secondary/50',
                bodyClassName,
              )}
              data-testid={`${dataTestId}-scroll`}
            >
              <Flex
                ref={contentRef}
                direction="column"
                gap={2}
                width="full"
                className="min-w-0"
              >
                {children}
              </Flex>
            </div>
          </div>
        </div>
      )}
    </Flex>
  );
}
